import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Calculator } from "lucide-react";
import { tools } from "@/content/tools";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { EabBannerBlock } from "@/components/eab-banner";

export const Route = createFileRoute("/hr-calculators/")({
  head: () => ({
    meta: [
      { title: "Free UK HR Calculators, Redundancy, Holiday, SSP" },
      {
        name: "description",
        content:
          "Free, interactive UK HR calculators: statutory redundancy pay, holiday entitlement and SSP, all using 2026/27 rates.",
      },
      { property: "og:title", content: "Free UK HR Calculators" },
      { property: "og:description", content: "Statutory redundancy, holiday and SSP calculators for UK employers." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-calculators" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-calculators" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Free UK HR Calculators",
          url: "https://www.hrservicecentre.co.uk/hr-calculators",
          description:
            "Free interactive UK HR calculators for statutory redundancy, holiday entitlement, SSP and more, updated for 2026/27.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: tools.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://www.hrservicecentre.co.uk/hr-calculators/${t.slug}`,
              name: t.name,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hrservicecentre.co.uk/hr-resources" },
            { "@type": "ListItem", position: 3, name: "HR Calculators", item: "https://www.hrservicecentre.co.uk/hr-calculators" },
          ],
        }),
      },
    ],
  }),
  component: ToolsIndex,
});

function ToolsIndex() {
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Resources", path: "/hr-resources" },
          { label: "Calculators", path: "/hr-calculators" },
        ]}
        eyebrow="Calculators"
        title="Free HR Calculators for UK Employers"
        subtitle="Instant HR calculators, updated for the 2026/27 statutory rates."
        intro="Free, instant calculators for the questions UK employers ask us most. All updated for the 2026/27 statutory rates."
      />

      <section className="px-6 py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((t) => (
            <Link
              key={t.slug}
              to="/hr-calculators/$slug"
              params={{ slug: t.slug }}
              className="group flex flex-col rounded-2xl bg-card p-8 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:ring-accent/30"
            >
              <span className="mb-6 inline-flex size-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                <Calculator className="size-5" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.category}</span>
              <h2 className="mt-2 font-serif text-xl text-brand">{t.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.shortDescription}</p>
            </Link>
          ))}
        </div>
      </section>

      <EabBannerBlock membership source="calculators_hub" />

      <InternalLinksHub
        heading="Everything UK employers need from an HR partner"
        intro="Browse our most-requested services, sector pages and free resources, all written by senior HR and employment law specialists."
      />
    </>
  );
}
